import React, { useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, Loader2, X, Upload, FileText, Search, BookOpen, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { aiAgentsApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";

const CATEGORY_COLOR = {
  faq: "bg-blue-50 text-blue-700 border-blue-200",
  membership: "bg-violet-50 text-violet-700 border-violet-200",
  financing: "bg-emerald-50 text-emerald-700 border-emerald-200",
  service: "bg-amber-50 text-amber-700 border-amber-200",
  warranty: "bg-cyan-50 text-cyan-700 border-cyan-200",
  policy: "bg-rose-50 text-rose-700 border-rose-200",
  other: "bg-slate-100 text-slate-600 border-slate-200",
};
const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);
const inputCls = "w-full h-9 px-3 rounded-lg border border-slate-300 text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400";

export default function KnowledgeBase({ meta, isAdmin }) {
  const cats = meta.kbCategories || [];
  const catLabel = (k) => (cats.find((c) => c.key === k) || {}).label || k;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {} | item

  const load = async () => {
    setLoading(true);
    try { setItems(await aiAgentsApi.listKnowledge()); }
    catch (e) { err(e, "Failed to load knowledge base."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let active = true;
    (async () => { await Promise.resolve(); if (active) load(); })();
    return () => { active = false; };
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this knowledge item?")) return;
    try { await aiAgentsApi.removeKnowledge(id); toast.success("Deleted."); load(); }
    catch (e) { err(e, "Could not delete."); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5" data-testid="knowledge-base">
      {/* Left: list */}
      <div className="lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] text-slate-500">{items.length} item{items.length === 1 ? "" : "s"} · searched by the agent during calls</div>
          {isAdmin && (
            <Button data-testid="add-knowledge-btn" onClick={() => setEditor({})} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-1.5" /> Add knowledge
            </Button>
          )}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center" data-testid="kb-empty">
            <BookOpen className="h-9 w-9 text-slate-300 mx-auto mb-3" />
            <div className="font-semibold text-slate-700">No knowledge yet</div>
            <div className="text-[13px] text-slate-500 mt-1 max-w-sm mx-auto">Add FAQs, financing, warranty and policy info so your agent can answer customer questions accurately.</div>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="kb-list">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`kb-card-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      {it.source === "upload" ? <Upload className="h-4.5 w-4.5 text-slate-500" /> : <FileText className="h-4.5 w-4.5 text-slate-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{it.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${CATEGORY_COLOR[it.category] || CATEGORY_COLOR.other}`}>{catLabel(it.category)}</span>
                        <span className="text-[11.5px] text-slate-400">{it.chunkCount} chunk{it.chunkCount === 1 ? "" : "s"} · {it.contentChars} chars</span>
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setEditor(it)} data-testid={`kb-edit-${it.id}`} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`kb-delete-${it.id}`} className="p-1.5 rounded-md text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right: test search */}
      <div className="lg:col-span-1">
        <TestSearch catLabel={catLabel} catColor={CATEGORY_COLOR} />
      </div>

      {editor && isAdmin && (
        <KbEditor meta={meta} item={editor} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />
      )}
    </div>
  );
}

function TestSearch({ catLabel, catColor }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const r = await aiAgentsApi.searchKnowledge(q.trim(), 4);
      setResults(r.results || []);
    } catch (e) { err(e, "Search failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sticky top-4" data-testid="kb-test-search">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-indigo-600" />
        <div className="font-semibold text-slate-800 text-[14px]">Test semantic search</div>
      </div>
      <p className="text-[12px] text-slate-500 mb-3">Ask a question the way a customer would — see what your agent will retrieve.</p>
      <div className="flex gap-2">
        <input
          data-testid="kb-search-input"
          value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="e.g. Do you offer financing?"
          className={inputCls}
        />
        <Button data-testid="kb-search-btn" onClick={run} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 px-3">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      <div className="mt-3 space-y-2" data-testid="kb-search-results">
        {results === null ? null : results.length === 0 ? (
          <div className="text-[12.5px] text-slate-400 text-center py-4">No matches. Add more knowledge or rephrase.</div>
        ) : (
          results.map((r, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`kb-result-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full border ${catColor[r.category] || catColor.other}`}>{catLabel(r.category)}</span>
                <span className="text-[10.5px] text-slate-400">match {Math.round((r.score || 0) * 100)}%</span>
              </div>
              <div className="text-[12px] font-semibold text-slate-700">{r.title}</div>
              <div className="text-[12px] text-slate-500 line-clamp-3 mt-0.5">{r.text}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function KbEditor({ meta, item, onClose, onSaved }) {
  const isNew = !item.id;
  const cats = meta.kbCategories || [];
  const [mode, setMode] = useState("text"); // text | upload (upload only for new)
  const [title, setTitle] = useState(item.title || "");
  const [category, setCategory] = useState(item.category || "faq");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(!isNew);

  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      try {
        const full = await aiAgentsApi.getKnowledge(item.id);
        if (active) setContent(full.content || "");
      } catch { /* ignore */ }
      finally { if (active) setLoadingDoc(false); }
    })();
    return () => { active = false; };
  }, [isNew, item.id]);

  const save = async () => {
    if (!title.trim() && mode === "text") { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      if (isNew && mode === "upload") {
        if (!file) { toast.error("Choose a file to upload."); setSaving(false); return; }
        await aiAgentsApi.uploadKnowledge(file, title, category);
      } else if (isNew) {
        await aiAgentsApi.createKnowledge({ title, category, content });
      } else {
        await aiAgentsApi.updateKnowledge(item.id, { title, category, content });
      }
      toast.success(isNew ? "Knowledge added." : "Saved.");
      onSaved();
    } catch (e) { err(e, "Could not save."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="kb-editor-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between z-10">
          <h3 className="font-semibold text-slate-800">{isNew ? "Add knowledge" : "Edit knowledge"}</h3>
          <button onClick={onClose} data-testid="kb-editor-close" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4 flex-1">
          {isNew && (
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              {[["text", "Paste text", FileText], ["upload", "Upload file", Upload]].map(([k, label, Icon]) => (
                <button key={k} data-testid={`kb-mode-${k}`} onClick={() => setMode(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-medium ${mode === k ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Title</label>
              <input data-testid="kb-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Financing Options" className={inputCls} />
            </div>
            <div>
              <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Category</label>
              <select data-testid="kb-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {isNew && mode === "upload" ? (
            <div>
              <label className="block text-[12.5px] font-medium text-slate-700 mb-1">File</label>
              <input
                data-testid="kb-file-input" type="file"
                accept=".txt,.md,.csv,.pdf,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-[13px] text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-[13px] file:font-medium hover:file:bg-indigo-100"
              />
              <div className="text-[11.5px] text-slate-400 mt-1">PDF, DOCX, TXT, MD or CSV · 15 MB max. Text is extracted and embedded for search.</div>
            </div>
          ) : (
            <div>
              <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Content</label>
              {loadingDoc ? (
                <div className="h-40 flex items-center justify-center"><Loader2 className="h-5 w-5 text-indigo-500 animate-spin" /></div>
              ) : (
                <textarea
                  data-testid="kb-content-input" rows={12} value={content} onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste the FAQ, financing terms, warranty details, policies…"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 resize-y"
                />
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="kb-save-btn" className="bg-indigo-600 hover:bg-indigo-700">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {isNew ? "Add" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
